// DEPENDENCIES
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as _ from 'lodash';
import moment from 'moment';
// COMPONENTS
import { Modal, Button, Spinner, Form, Container, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import Flatpickr from "react-flatpickr";
// CUSTOM COMPONENTS
import JobScheduleConflictModal from './JobScheduleConflictModal';
// SERVICES AND HELPERS
import * as jobService from '../../../../../services/service/jobService';
import * as jobTeamMemberService from '../../../../../services/service/jobTeamMemberService';
import * as helper from '../../../../../helpers/helper';
import * as jobScheduleService from '../../../../../services/service/jobScheduleService';
import JobScheduleValidator from '../../../../../helpers/validators/JobScheduleValidator';

const JobScheduleDetailBlock = (props) => {
    const { showScheduleBottomSheet, setShowScheduleBottomSheet, jobId, showAlert, fetchJobSchedules } = props;
    const initialValues = {
        jobId: jobId,
        jobTeamMemberIds: [],
        jobLocationId: null,
        scheduleDate: [],
        startTime: [],
        endTime: [],
        additionalNotes: '',
    }
    const [teamMemberOptions, setTeamMemberOptions] = useState([]);
    const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState([]);
    const [scheduleType, setScheduleType] = useState("single");
    const [scheduleModel, setScheduleModel] = useState(initialValues);
    const [overrideScheduleIds, setOverrideScheduleIds] = useState([]);
    const [conflictedJobSchedules, setConflictedJobSchedules] = useState([]);
    const [isJobConflictModalOpen, setIsJobConflictModalOpen] = useState(false);
    const [skipScheduleIds, setSkipScheduleIds] = useState([]);

    useEffect(() => {
        fetchTeamMembers();
        fetchJobLocations();
    }, []);

    const fetchTeamMembers = () => {
        jobTeamMemberService.getJobTeamMembers(jobId).then(res => {
            setTeamMemberOptions(res.map(x => helper.setToOptionModel(x.name + ' (' + x.role + (x.isFormLead ? ' / Form Lead' : "") + ')', x.userId)));
        });
    }

    const fetchJobLocations = () => {
        jobService.getJobLocations(jobId).then(res => {
            setLocationOptions(res.map(x => helper.setToOptionModel(x.projectLocation, x.id)));
        });
    }

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: scheduleModel,
        validationSchema: JobScheduleValidator,
        onSubmit: (values, { setStatus, setSubmitting }) => {
            convertFormValuesToModelAndSubmit(values, setSubmitting);
        }
    });

    const convertFormValuesToModelAndSubmit = (values, setSubmitting) => {
        var model = _.pick(values, ["jobId", "jobTeamMemberIds", "additionalNotes"]);
        model.proposalProjectLocationId = values.jobLocationId;
        model.scheduleDates = convertToJobScheduleDateDto(values);
        model.overrideScheduleIds = overrideScheduleIds.map(x => x.scheduleId);
        model.skipScheduleIds = skipScheduleIds;

        setSubmitting(true);
        jobScheduleService.saveJobSchedule(model).then(res => {
            if (res.success) {
                fetchJobSchedules();
                showAlert({ message: res.message, variant: 'success' });
                handleModalClose();
                return;
            }

            handleConflicts(res.jobSchedules);
        }).catch(ex => {
            showAlert({ message: ex.message, variant: 'error' });
            handleModalClose();
        }).finally(() => setSubmitting(false));
    }

    const convertToJobScheduleDateDto = (values) => {
        var allDates = [];

        var startDate = moment(values.scheduleDate[0]).startOf('day');
        if (values.scheduleDate[1]) {
            var endDate = moment(values.scheduleDate[1]).startOf('day');

            while (startDate.diff(endDate) <= 0) {
                allDates.push(checkIfAnotherDayAndConvert(moment(startDate), moment(values.startTime[0]), moment(values.endTime[0])));

                startDate = startDate.add(1, 'days');
            }
        } else {
            allDates.push(checkIfAnotherDayAndConvert(moment(values.scheduleDate[0]), moment(values.startTime[0]), moment(values.endTime[0])));
        }

        return allDates;
    }

    const checkIfAnotherDayAndConvert = (date, startTime, endTime) => {
        var firstDate = date.format("MM/DD/YYYY ") + startTime.format("hh:mm a");
        var endDate = ((startTime.diff(endTime) > 0) ? date.add(1, 'days').format("MM/DD/YYYY ") : date.format("MM/DD/YYYY ")) + endTime.format("hh:mm a");

        return { startDate: firstDate, endDate: endDate };
    }

    const handleConflicts = (conflictedJobSchedulesToSet) => {
        setConflictedJobSchedules(conflictedJobSchedulesToSet);
        setShowScheduleBottomSheet(false);
        setTimeout(() => {
            setIsJobConflictModalOpen(true);
        }, 250)
    }

    const handleModalClose = () => {
        setScheduleType("single");
        setSelectedLocation([]);
        setSelectedTeamMembers([]);
        setOverrideScheduleIds([]);
        setSkipScheduleIds([]);
        setIsJobConflictModalOpen(false);
        formik.resetForm();
        setShowScheduleBottomSheet(false);
        setScheduleModel(initialValues);
    }

    return (
        <>
            <JobScheduleConflictModal
                isJobConflictModalOpen={isJobConflictModalOpen}
                setIsJobConflictModalOpen={setIsJobConflictModalOpen}
                conflictedJobSchedules={conflictedJobSchedules}
                overrideScheduleIds={overrideScheduleIds}
                setOverrideScheduleIds={setOverrideScheduleIds}
                formik={formik}
                skipScheduleIds={skipScheduleIds}
                setSkipScheduleIds={setSkipScheduleIds}
                handleModalClose={handleModalClose}
            />
            <form className="" onSubmit={formik.handleSubmit}>
                <div className="form">
                    <Form.Group controlId="jobTeamMember">
                        <Form.Label className="fw-500">Team Members</Form.Label>
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            value={selectedTeamMembers}
                            options={teamMemberOptions}
                            onChange={option => {
                                setSelectedTeamMembers(option);
                                formik.setFieldValue("jobTeamMemberIds", option ? option.map(x => x.value) : [])
                            }}
                            name="teamMembers"
                            placeholder="Select Team Members"
                        />
                        {formik.touched.jobTeamMemberIds && formik.errors.jobTeamMemberIds ? (
                            <div className="invalid-feedback display-block">
                                {formik.errors.jobTeamMemberIds}
                            </div>
                        ) : null}
                    </Form.Group>
                    <Form.Group controlId="jobLocation">
                        <Form.Label className="fw-500">Select a Job Location</Form.Label>
                        <Select
                            value={selectedLocation}
                            options={locationOptions}
                            onChange={(option) => {
                                setSelectedLocation(option);
                                formik.setFieldValue("jobLocationId", option ? option.value : null);
                            }}
                            name="location"
                            placeholder="Select Job Location"
                        />
                        {formik.touched.jobLocationId && formik.errors.jobLocationId ? (
                            <div className="invalid-feedback display-block">
                                {formik.errors.jobLocationId}
                            </div>
                        ) : null}
                    </Form.Group>
                    <Form.Group controlId="scheduleType">
                        <Form.Label className="fw-500">Select Schedule Type</Form.Label>
                        <div key={`inline-radio`}>
                            <Form.Check
                                inline
                                label="Single Date"
                                name="type"
                                type='radio'
                                checked={scheduleType === "single"}
                                value={"single"}
                                onChange={(e) => {
                                    setScheduleType(e.target.value)
                                    formik.setFieldValue("scheduleDate", []);
                                }}
                                id={`inline-radio-1`}
                            />
                            <Form.Check
                                inline
                                label="Date Range"
                                name="type"
                                type='radio'
                                value={"range"}
                                checked={scheduleType === "range"}
                                onChange={(e) => {
                                    setScheduleType(e.target.value);
                                    formik.setFieldValue("scheduleDate", []);
                                }}
                                id={`inline-radio-2`}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group controlId="date">
                        <Form.Label className="fw-500">Select Date</Form.Label>
                        <Flatpickr
                            placeholder="MM/DD/YYYY"
                            options={{
                                dateFormat: "m/d/Y",
                                mode: scheduleType,
                                // minDate: moment().format("MM/DD/YYYY")
                            }}
                            value={formik.getFieldProps("scheduleDate").value}
                            onChange={date => {
                                if (date.length < 2 && scheduleType === "range") {
                                    return;
                                }
                                formik.setFieldValue("scheduleDate", date);
                            }}
                            className={`form-control form-control-lg form-control-solid ${helper.getInputClasses(formik, "scheduleDate")}`}
                        />
                        {formik.touched.scheduleDate && formik.errors.scheduleDate ? (
                            <div className="invalid-feedback display-block">
                                {formik.errors.scheduleDate}
                            </div>
                        ) : null}
                    </Form.Group>
                    <Form.Group>
                        <Row>
                            <Col>
                                <Form.Label className="fw-500">Start Time</Form.Label>
                                <Flatpickr
                                    placeholder="Select Start Time"
                                    options={{
                                        mode: "time",
                                        enableTime: true,
                                        noCalendar: true,
                                        dateFormat: "H:i",
                                        time_24hr: true
                                    }}
                                    value={formik.getFieldProps("startTime").value}
                                    onChange={date => {
                                        formik.setFieldValue("startTime", date);
                                    }}
                                    className={`form-control form-control-lg form-control-solid ${helper.getInputClasses(formik, "startTime")}`}
                                />
                                {formik.touched.startTime && formik.errors.startTime ? (
                                    <div className="invalid-feedback display-block">
                                        {formik.errors.startTime}
                                    </div>
                                ) : null}
                            </Col>
                            <Col>
                                <Form.Label className="fw-500">End Time</Form.Label>
                                <Flatpickr
                                    placeholder="Select End Time"
                                    options={{
                                        mode: "time",
                                        enableTime: true,
                                        noCalendar: true,
                                        dateFormat: "H:i",
                                        time_24hr: true,
                                    }}
                                    value={formik.getFieldProps("endTime").value}
                                    onChange={date => {
                                        formik.setFieldValue("endTime", date);
                                    }}
                                    className={`form-control form-control-lg form-control-solid ${helper.getInputClasses(formik, "endTime")}`}
                                />
                                {formik.touched.endTime && formik.errors.endTime ? (
                                    <div className="invalid-feedback display-block">
                                        {formik.errors.endTime}
                                    </div>
                                ) : null}
                            </Col>
                        </Row>
                    </Form.Group>
                    <Form.Group controlId="formBasicPassword">
                        <Form.Label>Additional Instructions</Form.Label>
                        <Form.Control
                            as="textarea"
                            placeholder="Leave an additional instruction here"
                            style={{ height: '100px' }}
                            name="additionalNotes"
                            {...formik.getFieldProps("additionalNotes")}
                        />
                    </Form.Group>
                </div>
                <div className="content-container--actions">
                    <Button variant="secondary" onClick={handleModalClose}>
                        Close
                    </Button>
                    <button
                        type="submit"
                        className="btn btn-success ml-3"
                        disabled={
                            formik.isSubmitting || (formik.touched && !formik.isValid)
                        }
                    >
                        Schedule
                        {formik.isSubmitting}
                    </button>
                </div>
            </form>
        </>
    )
}

export default JobScheduleDetailBlock;